import { useRef, useState } from "react";
import { Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatErr } from "@/lib/api";
import { ImageUpload } from "@/components/DesignPanel";
import { ImageAdjustButton } from "@/components/ImageAdjust";

const inputCls = "w-full h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const label = "block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2";

const MultiImage = ({ images, onChange, max, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    const list = [...files].slice(0, max - images.length);
    if (!list.length) return;
    setBusy(true);
    const added = [];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try { added.push((await api.post("/images/upload", fd)).data.path); }
      catch (e) { toast.error(formatErr(e)); }
    }
    onChange([...images, ...added]);
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {images.map((p, i) => (
        <div key={p} className="relative group">
          <img src={assetUrl(p)} alt="" className="h-16 w-24 object-contain rounded-md border border-[#242C3D] bg-black/30" />
          <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(images.filter((_, j) => j !== i))}
            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center opacity-80 hover:opacity-100"><X className="w-3 h-3" /></button>
        </div>
      ))}
      {images.length < max && (
        <>
          <input ref={ref} type="file" accept="image/*" multiple className="hidden" data-testid={`${testid}-input`} onChange={(e) => upload(e.target.files)} />
          <button type="button" data-testid={`${testid}-add`} disabled={busy} onClick={() => ref.current?.click()}
            className="h-16 w-24 rounded-md border border-dashed border-[#2f3a50] hover:border-indigo-500 text-slate-400 hover:text-white grid place-items-center transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </button>
        </>
      )}
    </div>
  );
};

const SIZES = [["sm", "S"], ["md", "M"], ["lg", "L"], ["xl", "XL"]];

const TopHeaderEditor = ({ th, setTh }) => (
  <div className="space-y-3" data-testid="top-header-editor">
    <span className={label}>Header above banner image</span>
    <input data-testid="top-header-text-input" value={th.text} onChange={(e) => setTh({ text: e.target.value })} placeholder="e.g. Cisco Duo presents · Live virtual summit" className={inputCls} />
    <div className="flex flex-wrap items-center gap-3">
      <input type="color" data-testid="top-header-color-picker" value={th.color || "#FFFFFF"} onChange={(e) => setTh({ color: e.target.value })} className="h-9 w-11 rounded-md bg-transparent border border-[#242C3D] cursor-pointer p-1" />
      <input data-testid="top-header-color-input" value={th.color} onChange={(e) => setTh({ color: e.target.value })} placeholder="#FFFFFF" className="h-9 w-24 px-2 rounded-md bg-[#0B0F17] border border-[#242C3D] text-xs font-mono focus:outline-none" />
      <div className="flex gap-1.5">
        {SIZES.map(([v, l]) => (
          <button key={v} type="button" data-testid={`top-header-size-${v}`} onClick={() => setTh({ size: v })}
            className={`h-9 w-10 rounded-full text-xs border transition-colors ${th.size === v ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
    </div>
  </div>
);

const GlobalThumbToggle = ({ on, toggle, canEdit }) => (
  <label className={`mt-3 flex items-start gap-2 text-xs text-slate-300 ${canEdit ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}>
    <input type="checkbox" data-testid="global-hide-thumbnail-toggle" checked={on} disabled={!canEdit} onChange={(e) => toggle(e.target.checked)} className="accent-indigo-500 mt-0.5" />
    <span>Hide the video thumbnail on <b>all</b> landing pages <span className="text-slate-500">({canEdit ? "global setting, saved instantly" : "workspace setting — only admins can change it"})</span></span>
  </label>
);

export const MediaEditor = ({ form, set, hideThumb, setHideThumb, canEditGlobal = true }) => {
  const tk = form.takeaways;
  const ist = form.image_styles || {};
  const setSt = (k) => (v) => set({ image_styles: { ...ist, [k]: v } });
  return (
    <div className="space-y-8" data-testid="media-editor">
      <TopHeaderEditor th={form.top_header} setTh={(patch) => set({ top_header: { ...form.top_header, ...patch } })} />
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <span className={label}>Hero banner image</span>
          <div className="flex items-center gap-2">
            <ImageUpload value={form.hero_image_path} onChange={(p) => set({ hero_image_path: p })} testid="media-hero-upload" emptyLabel="Upload banner" />
            <ImageAdjustButton src={assetUrl(form.hero_image_path)} value={ist.hero} onChange={setSt("hero")} aspect="aspect-[21/7]" testid="media-hero-adjust" title="Adjust banner image" />
          </div>
          <p className="text-xs text-slate-500 mt-1.5">Wide image shown at the top of the page.</p>
        </div>
        <div>
          <span className={label}>Video thumbnail</span>
          <div className="flex items-center gap-2">
            <ImageUpload value={form.thumbnail_path} onChange={(p) => set({ thumbnail_path: p })} testid="media-thumbnail-upload" emptyLabel="Upload thumbnail" />
            <ImageAdjustButton src={assetUrl(form.thumbnail_path)} value={ist.thumbnail} onChange={setSt("thumbnail")} aspect="aspect-video" allowHeight={false} testid="media-thumbnail-adjust" title="Adjust video thumbnail" />
          </div>
          <p className="text-xs text-slate-500 mt-1.5">Replaces the default locked-video image.</p>
          <GlobalThumbToggle on={hideThumb} toggle={setHideThumb} canEdit={canEditGlobal} />
        </div>
      </div>

      <div className="space-y-3">
        <span className={label}>Key takeaways section</span>
        <input data-testid="media-takeaways-heading" value={tk.heading} onChange={(e) => set({ takeaways: { ...tk, heading: e.target.value } })} placeholder="Key takeaways" className={inputCls} />
        <textarea data-testid="media-takeaways-text" rows={4} value={tk.text} onChange={(e) => set({ takeaways: { ...tk, text: e.target.value } })}
          placeholder={"One takeaway per line, e.g.\n- Learn how to measure service maturity\n- Explore AI-driven automation"} className={`${inputCls} h-auto py-2 resize-none text-xs`} />
        <div className="flex items-center gap-3">
          <ImageUpload value={tk.image_path} onChange={(p) => set({ takeaways: { ...tk, image_path: p } })} testid="media-takeaways-image" emptyLabel="Section image" />
          <ImageAdjustButton src={assetUrl(tk.image_path)} value={ist.takeaways} onChange={setSt("takeaways")} aspect="aspect-[4/3]" testid="media-takeaways-adjust" title="Adjust takeaways image" />
          <span className="text-xs text-slate-500">Section is hidden when the text is empty.</span>
        </div>
      </div>

      <div className="space-y-3">
        <span className={label}>Image gallery (up to 12)</span>
        <input data-testid="media-gallery-heading" value={form.gallery.heading} onChange={(e) => set({ gallery: { ...form.gallery, heading: e.target.value } })} placeholder="Gallery" className={inputCls} />
        <MultiImage images={form.gallery.images} onChange={(images) => set({ gallery: { ...form.gallery, images } })} max={12} testid="media-gallery" />
      </div>

      <div className="space-y-3">
        <span className={label}>Partner / sponsor logos (up to 16)</span>
        <input data-testid="media-partners-heading" value={form.partners.heading} onChange={(e) => set({ partners: { ...form.partners, heading: e.target.value } })} placeholder="Brought to you by" className={inputCls} />
        <MultiImage images={form.partners.images} onChange={(images) => set({ partners: { ...form.partners, images } })} max={16} testid="media-partners" />
      </div>
    </div>
  );
};
